import type { KeyboardEvent, RefObject } from 'react';

/** What Tab can reach: file inputs are hidden pickers behind their buttons. */
const FOCUSABLE =
  'button:not([disabled]), input:not([disabled]):not([type=file]):not([type=hidden]), textarea:not([disabled]), a[href], [tabindex]:not([tabindex="-1"])';

/**
 * Keeps Tab inside `ref` (a modal): Tab and Shift+Tab wrap at its ends. The
 * returned handler goes on the dialog's `onKeyDown`. A menu or the lightbox
 * draws outside or over the dialog and keeps its own Tab.
 */
export function useFocusTrap(
  ref: RefObject<HTMLElement>
): (event: KeyboardEvent<HTMLElement>) => void {
  return event => {
    const dialog = ref.current;
    if (event.key !== 'Tab' || event.defaultPrevented || !dialog) return;
    if (!dialog.contains(event.target as Node)) return;
    const items = Array.from(dialog.querySelectorAll<HTMLElement>(FOCUSABLE));
    if (items.length === 0) return;
    const at = items.indexOf(document.activeElement as HTMLElement);
    const back = event.shiftKey;
    const edge = back ? 0 : items.length - 1;
    const target = back ? items[items.length - 1] : items[0];
    if (at !== -1 && at !== edge) return;
    if (at === -1) {
      // Focus is on the dialog or an element the list cannot match (the message
      // list). Let the browser move when a known item lies in the direction of
      // travel; wrap only when none does, where the native move would leave
      // the modal.
      const active = document.activeElement;
      const direction = back
        ? Node.DOCUMENT_POSITION_PRECEDING
        : Node.DOCUMENT_POSITION_FOLLOWING;
      if (
        active !== null &&
        items.some(item => !!(active.compareDocumentPosition(item) & direction))
      ) {
        return;
      }
    }
    event.preventDefault();
    target.focus();
  };
}
